'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import type { GameTrackingInfo } from '@/types/games';
import {
    EVENT_TYPE,
    getGameElementDataFromZoneGame,
    SNOWPLOW_CONTEXT_MAPPING,
    type GameZoneSnowplowConfig,
    type GameZoneSnowplowStaticConfig
} from '@/tracking/snowplow/utils';
import { useSnowplowUm } from '@/tracking/snowplow';
import { ensureCoreEventsInitialized } from '@/hooks/use-core-events';
import {
    buildGameTileMap,
    getOnScreenFlagsForZoneGames,
    getScrollableFromZoneContainer
} from '@/utils/tracking/game-zone-tile-visibility';
import { getGameZoneInteractionProps } from '@/utils/tracking/game-zone-interaction';
import { useDebounce } from '@/hooks/use-debounce';

const DEFAULT_GRID_SELECTOR = '.grid-zone-layout';

const SCROLL_DEBOUNCE_MS = 300;

export type UseGameZoneImpressionProps = {
    containerRef: React.RefObject<HTMLElement | null>;
    items: GameTrackingInfo[];
    zoneConfig: GameZoneSnowplowStaticConfig;
    gridSelector?: string;
};

/**
 * Fires `game_zone_impression` once when the zone container intersects the viewport (`IntersectionObserver`,
 * `threshold: 0`). Per-game `on_screen` comes from `getOnScreenFlagsForZoneGames` (thumbnail visibility vs
 * viewport, carousel band, and prev/next arrow strip). Sends via `useSnowplowUm` → Core Events `logEvent`;
 * awaits `ensureCoreEventsInitialized` first (same pattern as top nav). Resets the "sent" flag when the
 * route or item count changes.
 */
export const useGameZoneImpression = ({
    containerRef,
    items,
    zoneConfig,
    gridSelector = DEFAULT_GRID_SELECTOR
}: UseGameZoneImpressionProps) => {
    const pathname = usePathname();
    const impressionSentRef = useRef(false);
    const tileMapRef = useRef<Map<string, HTMLElement>>(new Map());
    const { sendEventToSnowplow } = useSnowplowUm();

    useEffect(() => {
        tileMapRef.current = buildGameTileMap(containerRef.current, items, gridSelector);
    }, [items, containerRef, gridSelector]);

    const sendZoneSnowplowEvent = async (
        event: typeof EVENT_TYPE.GAME_ZONE_IMPRESSION | typeof EVENT_TYPE.GAME_ZONE_SCROLL,
        action: 'impression' | 'scroll'
    ) => {
        if (!items.length) return;

        const onScreenFlags = getOnScreenFlagsForZoneGames(
            containerRef.current,
            items,
            gridSelector,
            tileMapRef.current
        );

        const zoneInteractionProps = getGameZoneInteractionProps({
            action,
            element: containerRef.current,
            pathname: pathname ?? '',
            fallbackZoneName: zoneConfig.zoneName,
            fallbackZoneType: zoneConfig.zoneType,
            fallbackZoneLayout: zoneConfig.zoneLayout
        });

        const snowplowZoneConfig: GameZoneSnowplowConfig = {
            ...zoneConfig,
            zoneLayout: zoneInteractionProps.zoneLayout,
            zoneName: zoneInteractionProps.zoneName,
            zonePosition: zoneInteractionProps.zonePosition
        };

        const gameDataContexts = items.map((game, index) => ({
            contextType: SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION,
            contextProps: getGameElementDataFromZoneGame(
                snowplowZoneConfig,
                index + 1,
                onScreenFlags[index] ?? false,
                game
            )
        }));

        await ensureCoreEventsInitialized();
        sendEventToSnowplow({
            event,
            eventProps: zoneInteractionProps,
            contexts: gameDataContexts
        });
    };

    const sendEventRef = useRef(sendZoneSnowplowEvent);
    sendEventRef.current = sendZoneSnowplowEvent;

    const debouncedScrollHandler = useDebounce(() => {
        (async () => {
            try {
                await sendEventRef.current(EVENT_TYPE.GAME_ZONE_SCROLL, 'scroll');
            } catch {
                // Logged in ensureCoreEventsInitialized.
            }
        })();
    }, SCROLL_DEBOUNCE_MS);

    useEffect(() => {
        const root = containerRef.current;
        if (!root) return () => {};

        const scrollable = getScrollableFromZoneContainer(root);
        if (!scrollable) return () => {};

        scrollable.addEventListener('scroll', debouncedScrollHandler, { passive: true });
        scrollable.addEventListener('draggable-swipe', debouncedScrollHandler);
        return () => {
            scrollable.removeEventListener('scroll', debouncedScrollHandler);
            scrollable.removeEventListener('draggable-swipe', debouncedScrollHandler);
        };
    }, [pathname, items.length, gridSelector, containerRef, debouncedScrollHandler]);

    useEffect(() => {
        impressionSentRef.current = false;
        const el = containerRef.current;
        if (!el || !items.length) {
            return () => {};
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry?.isIntersecting || impressionSentRef.current) {
                    return;
                }
                impressionSentRef.current = true;
                (async () => {
                    try {
                        await sendEventRef.current(EVENT_TYPE.GAME_ZONE_IMPRESSION, 'impression');
                    } catch {
                        // Logged in ensureCoreEventsInitialized.
                    }
                })();
            },
            { root: null, threshold: 0 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [pathname, items.length, gridSelector, containerRef]);
};
